import { useState, useCallback, useEffect } from 'react'

/**
 * useTour Hook (T331)
 * Manages feature tour step progression and completion tracking.
 *
 * Based on FLOW-02-ONBOARDING.md §5 - Feature Tour
 *
 * Features:
 * - Tour step management (4 steps per spec)
 * - localStorage completion tracking
 * - Skip and dismiss functionality
 */

const STORAGE_KEY = 'tour_completed'

export interface TourStep {
  /** Unique identifier for the step */
  id: string
  /** Whether this step is a modal (vs highlight) */
  modal: boolean
  /** CSS selector for element to highlight (if not modal) */
  highlight?: string
}

/**
 * Default tour steps per FLOW-02 §5
 */
export const DEFAULT_TOUR_STEPS: TourStep[] = [
  { id: 'welcome', modal: true },
  { id: 'create-button', modal: false, highlight: '.btn-create-rule' },
  { id: 'rule-types', modal: true },
  { id: 'notifications', modal: false, highlight: '.nav-notifications' },
]

export interface TourState {
  /** Whether the tour is currently active */
  isActive: boolean
  /** Current step index (0-based) */
  currentStep: number
  /** Whether the tour has been completed */
  isComplete: boolean
  /** Total number of steps */
  totalSteps: number
  /** Current step data */
  currentStepData: TourStep | null
}

export interface UseTourReturn extends TourState {
  /** Start the tour */
  startTour: () => void
  /** Go to next step */
  nextStep: () => void
  /** Go to previous step */
  previousStep: () => void
  /** Go to a specific step */
  goToStep: (step: number) => void
  /** Skip/dismiss the tour */
  skipTour: () => void
  /** Complete the tour */
  completeTour: () => void
  /** Reset tour (for testing) */
  resetTour: () => void
  /** Check if tour should auto-start */
  shouldAutoStart: () => boolean
}

export interface UseTourOptions {
  /** Custom tour steps (defaults to DEFAULT_TOUR_STEPS) */
  steps?: TourStep[]
  /** Auto-start tour if not completed */
  autoStart?: boolean
  /** Callback when tour completes */
  onComplete?: () => void
  /** Callback when tour is skipped */
  onSkip?: () => void
  /** Custom localStorage key (defaults to 'tour_completed') */
  storageKey?: string
}

export function useTour(options: UseTourOptions = {}): UseTourReturn {
  const {
    steps = DEFAULT_TOUR_STEPS,
    autoStart = false,
    onComplete,
    onSkip,
    storageKey,
  } = options

  const key = storageKey ?? STORAGE_KEY

  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  // Check completion status on mount
  useEffect(() => {
    const completed = localStorage.getItem(key)
    setIsComplete(completed === 'true')

    // Auto-start tour if enabled and not completed
    if (autoStart && completed !== 'true') {
      setIsActive(true)
    }
  }, [autoStart, key])

  const totalSteps = steps.length

  const currentStepData = isActive && currentStep < totalSteps
    ? steps[currentStep]
    : null

  /**
   * Start the tour
   */
  const startTour = useCallback(() => {
    setCurrentStep(0)
    setIsActive(true)
  }, [])

  /**
   * Go to next step
   * Completes tour if on last step
   */
  const nextStep = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      // On last step, complete the tour
      localStorage.setItem(key, 'true')
      setIsComplete(true)
      setIsActive(false)
      onComplete?.()
    }
  }, [currentStep, totalSteps, onComplete, key])

  /**
   * Go to previous step
   */
  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }, [currentStep])

  /**
   * Go to a specific step
   */
  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step)
    }
  }, [totalSteps])

  /**
   * Skip/dismiss the tour
   * Marks as complete to prevent future auto-starts
   */
  const skipTour = useCallback(() => {
    localStorage.setItem(key, 'true')
    setIsComplete(true)
    setIsActive(false)
    onSkip?.()
  }, [onSkip, key])

  /**
   * Complete the tour
   */
  const completeTour = useCallback(() => {
    localStorage.setItem(key, 'true')
    setIsComplete(true)
    setIsActive(false)
    onComplete?.()
  }, [onComplete, key])

  /**
   * Reset tour (for testing or re-showing)
   */
  const resetTour = useCallback(() => {
    localStorage.removeItem(key)
    setIsComplete(false)
    setCurrentStep(0)
    setIsActive(false)
  }, [key])

  /**
   * Check if tour should auto-start
   * Can be called to decide whether to trigger tour
   */
  const shouldAutoStart = useCallback(() => {
    return localStorage.getItem(key) !== 'true'
  }, [key])

  return {
    isActive,
    currentStep,
    isComplete,
    totalSteps,
    currentStepData,
    startTour,
    nextStep,
    previousStep,
    goToStep,
    skipTour,
    completeTour,
    resetTour,
    shouldAutoStart,
  }
}
